import type { ChatMessage } from './types';
import type { ContactFormPayload, QualificationOptionsPayload } from './api';

export type StoredMessage = ChatMessage;

export interface StoredChatHistory {

  messages: ChatMessage[];
  suggestions: string[];
  feedback: Record<number, 'positive' | 'negative'>;
  /**
   * The question the last reply asked, and the handoff it offered.
   *
   * Persisted with the transcript because they are part of the reply, not
   * decoration on top of it: a restored conversation ending in "which model are
   * you planning?" with no buttons under it asks the visitor something they
   * cannot answer, and a contact form that vanishes on refresh loses the lead
   * it was collecting.
   */
  qualification: QualificationOptionsPayload | null;
  /** A lead question offered as the last suggestion chip, until answered. */
  yourTurn?: QualificationOptionsPayload | null;
  contactForm: ContactFormPayload | null;
  /** Field values typed into that form but not yet submitted. */
  contactValues: Record<string, string>;
  expiresAt: number;
}

/** A stored Your Turn chip, when it can still be answered. */
function readYourTurn(value: any): QualificationOptionsPayload | null {
  return value && typeof value === 'object' && value.question_id && value.question &&
    Array.isArray(value.options) && value.options.length >= 2
    ? value
    : null;
}

export class ChatStorage {
  private storagePrefix: string;
  private ttlMs: number;
  private useLocalStorage: boolean;

  constructor(storagePrefix: string = 'vinfotech_chat_', ttlMinutes: number = 180, useLocalStorage: boolean = true) {
    this.storagePrefix = storagePrefix;
    this.ttlMs = ttlMinutes * 60 * 1000;
    this.useLocalStorage = useLocalStorage;
    this.cleanupExpiredSessions();
  }

  private getHistoryKey(sessionId: string): string {
    return `${this.storagePrefix}history_${sessionId}`;
  }

  private getStorage(): Storage | null {
    if (typeof window === 'undefined') return null;
    return this.useLocalStorage ? localStorage : sessionStorage;
  }

  /**
   * Scans storage for any expired chat sessions belonging to this widget and removes them
   * to avoid unbounded localStorage growth across many sessions.
   */
  cleanupExpiredSessions(): void {
    const storage = this.getStorage();
    if (!storage || typeof storage.length !== 'number') return;

    const prefix = `${this.storagePrefix}history_`;
    const keysToRemove: string[] = [];

    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const raw = storage.getItem(key);
            if (!raw) {
              keysToRemove.push(key);
              continue;
            }
            const parsed = JSON.parse(raw) as StoredChatHistory;
            if (!parsed || typeof parsed.expiresAt !== 'number' || Date.now() > parsed.expiresAt) {
              keysToRemove.push(key);
            }
          } catch {
            keysToRemove.push(key);
          }
        }
      }

      for (const key of keysToRemove) {
        storage.removeItem(key);
      }
    } catch (e) {
      console.warn('Failed to cleanup expired chat sessions:', e);
    }
  }

  /**
   * Prunes the oldest cached sessions to free up space in case of QuotaExceededError.
   */
  pruneOldestSessions(count: number = 2): void {
    const storage = this.getStorage();
    if (!storage || typeof storage.length !== 'number') return;

    const prefix = `${this.storagePrefix}history_`;
    const sessionEntries: Array<{ key: string; expiresAt: number }> = [];

    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const raw = storage.getItem(key);
            const parsed = raw ? (JSON.parse(raw) as StoredChatHistory) : null;
            sessionEntries.push({
              key,
              expiresAt: parsed?.expiresAt || 0
            });
          } catch {
            sessionEntries.push({ key, expiresAt: 0 });
          }
        }
      }

      sessionEntries.sort((a, b) => a.expiresAt - b.expiresAt);
      const toRemove = sessionEntries.slice(0, count);
      for (const entry of toRemove) {
        storage.removeItem(entry.key);
      }
    } catch (e) {
      console.warn('Failed to prune oldest sessions:', e);
    }
  }

  loadChatHistory(sessionId: string): {
    messages: ChatMessage[];
    suggestions: string[];
    feedback: Record<number, 'positive' | 'negative'>;
    qualification: QualificationOptionsPayload | null;
    yourTurn: QualificationOptionsPayload | null;
    contactForm: ContactFormPayload | null;
    contactValues: Record<string, string>;
  } {
    const emptyState = {
      messages: [],
      suggestions: [],
      feedback: {},
      qualification: null,
      yourTurn: null,
      contactForm: null,
      contactValues: {}
    };

    const storage = this.getStorage();
    if (!storage || typeof storage.getItem !== 'function' || !sessionId) {
      return emptyState;
    }

    try {
      const storedValue = storage.getItem(this.getHistoryKey(sessionId));
      if (!storedValue) {
        return emptyState;
      }

      const parsed = JSON.parse(storedValue) as StoredChatHistory;
      if (!parsed || typeof parsed.expiresAt !== 'number') {
        if (typeof storage.removeItem === 'function') {
          storage.removeItem(this.getHistoryKey(sessionId));
        }
        return emptyState;
      }

      if (Date.now() > parsed.expiresAt) {
        if (typeof storage.removeItem === 'function') {
          storage.removeItem(this.getHistoryKey(sessionId));
        }
        return emptyState;
      }

      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions : [],
        feedback: parsed.feedback && typeof parsed.feedback === 'object' ? parsed.feedback : {},
        // Options are only usable with at least two of them and an id to answer
        // against; anything less restores a row that cannot be sent. A typed-
        // answer question (buttons off) has none by design and needs only the id.
        qualification:
          parsed.qualification &&
          typeof parsed.qualification === 'object' &&
          parsed.qualification.question_id &&
          (parsed.qualification.answer_type === 'text' ||
            (Array.isArray(parsed.qualification.options) &&
              parsed.qualification.options.length >= 2))
            ? parsed.qualification
            : null,
        yourTurn: readYourTurn(parsed.yourTurn),
        contactForm:
          parsed.contactForm &&
          typeof parsed.contactForm === 'object' &&
          Array.isArray(parsed.contactForm.fields)
            ? parsed.contactForm
            : null,
        contactValues:
          parsed.contactValues && typeof parsed.contactValues === 'object'
            ? parsed.contactValues
            : {}
      };
    } catch (error) {
      console.error('Failed to load chat history from cache:', error);
      return emptyState;
    }
  }

  saveChatHistory(sessionId: string, state: {
    messages: ChatMessage[];
    suggestions: string[];
    feedback: Record<number, 'positive' | 'negative'>;
    qualification?: QualificationOptionsPayload | null;
    yourTurn?: QualificationOptionsPayload | null;
    contactForm?: ContactFormPayload | null;
    contactValues?: Record<string, string>;
  }): void {
    const storage = this.getStorage();
    if (!storage || typeof storage.setItem !== 'function' || !sessionId) {
      return;
    }

    const storageKey = this.getHistoryKey(sessionId);

    if (!state.messages.length && !state.suggestions.length
        && !state.qualification && !state.contactForm) {
      if (typeof storage.removeItem === 'function') {
        storage.removeItem(storageKey);
      }
      return;
    }

    const payload: StoredChatHistory = {
      messages: [...state.messages],
      suggestions: [...state.suggestions],
      feedback: { ...state.feedback },
      qualification: state.qualification ?? null,
      yourTurn: state.yourTurn ?? null,
      contactForm: state.contactForm ?? null,
      contactValues: state.contactValues ?? {},
      expiresAt: Date.now() + this.ttlMs
    };

    const serialized = JSON.stringify(payload);

    try {
      storage.setItem(storageKey, serialized);
    } catch (error) {
      console.warn('Failed to cache chat history, attempting to prune oldest sessions:', error);
      this.pruneOldestSessions(2);
      try {
        storage.setItem(storageKey, serialized);
      } catch (retryError) {
        console.error('Failed to cache chat history after pruning:', retryError);
      }
    }
  }

  clearChatHistory(sessionId: string): void {
    const storage = this.getStorage();
    if (!storage || typeof storage.removeItem !== 'function' || !sessionId) {
      return;
    }
    storage.removeItem(this.getHistoryKey(sessionId));
  }
}

